import { useState, useEffect } from 'react'
import { useNavigate, useBlocker } from 'react-router-dom'
import { Plus, Trash2, Printer, Search, X } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project, APris } from '../lib/api/types'
import { DEFAULT_GROUPS, isStandardGroup, withStandardGroups, type Group } from '../lib/aprisGroups'

const UNITS = ['tim', 'st', 'm', 'm²', 'm³', 'kg', 'ton', 'ls', '%']

type APrisField = 'kod' | 'benamning' | 'enhet' | 'pris'

const HUE_POOL = [160, 30, 330, 260, 100, 10, 200]

export default function APrisLista() {
  const navigate = useNavigate()
  const [items, setItems]     = useState<APris[]>([])
  const [groups, setGroups]   = useState<Group[]>(DEFAULT_GROUPS)
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [search, setSearch]   = useState('')
  const [dirty, setDirty]     = useState(false)
  const [newPrefix, setNewPrefix] = useState('')
  const [newLabel, setNewLabel]   = useState('')

  useEffect(() => {
    Promise.all([service.getProject(), service.getAPriser()])
      .then(([proj, priser]) => {
        setProject(proj)
        setItems(priser)
        setGroups(withStandardGroups(proj?.aprisGrupper ? (JSON.parse(proj.aprisGrupper) as Group[]) : DEFAULT_GROUPS))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm('Du har osparade ändringar. Vill du lämna utan att spara?')) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const addRow = (prefix: string) => {
    const groupItems = items.filter(i => (i.kod ?? '').startsWith(prefix))
    const nextNum = groupItems.length + 1
    const kod = `${prefix}-${String(nextNum).padStart(2, '0')}`
    const nextOrder = items.length > 0 ? Math.max(...items.map(i => i.sortOrder)) + 1 : 1
    setItems(prev => [...prev, { sortOrder: nextOrder, kod, benamning: '', enhet: 'tim', pris: null }])
    setDirty(true)
  }

  const removeRow = (sortOrder: number) => { setItems(prev => prev.filter(i => i.sortOrder !== sortOrder)); setDirty(true) }

  const updateRow = (sortOrder: number, field: APrisField, value: string) => {
    setDirty(true)
    const patch = field === 'pris' ? (value === '' ? null : parseFloat(value)) : value
    setItems(prev => prev.map(i =>
      i.sortOrder === sortOrder
        ? ({ ...i, [field]: patch } as APris)
        : i
    ))
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      await Promise.all([
        service.saveAPriser(items),
        service.saveProject({ ...project, aprisGrupper: JSON.stringify(groups) }),
      ])
      setDirty(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  const renameGroup = (prefix: string, label: string) => {
    setGroups(prev => prev.map(g => g.prefix === prefix ? { ...g, label } : g))
    setDirty(true)
  }

  const deleteGroup = (prefix: string) => {
    if (isStandardGroup(prefix)) return // standard groups (Arbete, UE) can't be removed
    setGroups(prev => prev.filter(g => g.prefix !== prefix))
    setDirty(true)
  }

  const addGroup = () => {
    const p = newPrefix.trim().toUpperCase()
    const l = newLabel.trim()
    if (!p || !l || groups.some(g => g.prefix === p)) return
    const usedHues = groups.map(g => g.hue)
    const hue = HUE_POOL.find(h => !usedHues.includes(h)) ?? HUE_POOL[groups.length % HUE_POOL.length]
    setGroups(prev => [...prev, { prefix: p, label: l, hue, c: 0.11 }])
    setNewPrefix('')
    setNewLabel('')
    setDirty(true)
  }

  const q = search.toLowerCase()
  const matchItem = (item: APris) =>
    !q ||
    (item.kod ?? '').toLowerCase().includes(q) ||
    (item.benamning ?? '').toLowerCase().includes(q)

  return (
    <div>
      <div className="page-header">
        <div>
          {projectLine && <div className="page-eyebrow">{projectLine}</div>}
          <h1 className="page-title">À-prislista</h1>
          <div className="page-sub">
            Avtalade enhetspriser och påslag för projektet.
            Används som referens när ÄTA-specifikationer prissätts.
          </div>
        </div>
        <div className="page-actions">
          <button className="icon-btn" onClick={() => navigate('/print/aprislista')}>
            <Printer size={14} />
            Skriv ut
          </button>
          {dirty && !saving && (
            <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>● Osparade ändringar</span>
          )}
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar…' : saved ? 'Sparat ✓' : 'Spara ändringar'}
          </button>
        </div>
      </div>

      {saved && <div className="notification notification-success"><span>À-prislistan sparades.</span></div>}
      {saveError && <div className="notification notification-error"><span>Fel: {saveError}</span></div>}

      {/* Search + meta */}
      <div className="view-toolbar" style={{ marginBottom: 20 }}>
        <div className="search-input">
          <Search size={13} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Sök kod eller benämning…" />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
          {items.length} rader totalt
        </span>
      </div>

      <datalist id="aprislista-units">
        {[...new Set([...UNITS, ...items.map(i => i.enhet).filter(Boolean)])].map(u => <option key={u} value={u} />)}
      </datalist>

      {/* Groups */}
      {groups.map(g => {
        const groupItems = items.filter(i => (i.kod ?? '').startsWith(g.prefix + '-'))
        const visible = groupItems.filter(matchItem)
        const swatchColor = `oklch(0.55 ${g.c} ${g.hue})`

        return (
          <div key={g.prefix} style={{ marginBottom: 24 }}>
            {/* Group header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: swatchColor, flexShrink: 0 }} />
              <input
                value={g.label}
                onChange={e => renameGroup(g.prefix, e.target.value)}
                style={{ fontSize: 14, fontWeight: 600, border: 'none', background: 'transparent', padding: '0 4px', borderRadius: 4, color: 'var(--ink)', outline: 'none', minWidth: 60 }}
                onFocus={e => e.target.style.background = 'var(--bg-2)'}
                onBlur={e => e.target.style.background = 'transparent'}
              />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-faint)' }}>
                {g.prefix}-koder · {groupItems.length} st
              </span>
              {groupItems.length === 0 && !isStandardGroup(g.prefix) && (
                <button
                  className="icon-button"
                  style={{ color: 'var(--ink-faint)', marginLeft: 'auto' }}
                  title="Ta bort grupp"
                  onClick={() => deleteGroup(g.prefix)}
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Card table */}
            <div className="table-card">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Kod</th>
                    <th>Benämning</th>
                    <th style={{ width: 100 }}>Enhet</th>
                    <th className="num" style={{ width: 140 }}>À-pris</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-faint)', padding: 20, fontSize: 12.5 }}>
                        {q ? 'Inga rader matchar sökningen' : 'Inga rader — klicka "+ Ny rad"'}
                      </td>
                    </tr>
                  )}
                  {visible.map(item => (
                    <tr key={item.sortOrder}>
                      <td>
                        <input
                          className="field-input"
                          style={{ fontFamily: 'var(--mono)', fontSize: 12.5, padding: '4px 8px' }}
                          value={item.kod ?? ''}
                          onChange={e => updateRow(item.sortOrder, 'kod', e.target.value)}
                          placeholder={`${g.prefix}-01`}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          style={{ padding: '4px 8px', fontSize: 13 }}
                          value={item.benamning ?? ''}
                          onChange={e => updateRow(item.sortOrder, 'benamning', e.target.value)}
                          placeholder="Benämning"
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          list="aprislista-units"
                          style={{ padding: '4px 8px', fontSize: 12.5 }}
                          value={item.enhet ?? ''}
                          onChange={e => updateRow(item.sortOrder, 'enhet', e.target.value)}
                          placeholder="enh"
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                          <input
                            className="field-input"
                            type="number"
                            style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 13, padding: '4px 8px', width: 90 }}
                            value={item.pris ?? ''}
                            onChange={e => updateRow(item.sortOrder, 'pris', e.target.value)}
                            placeholder="0"
                          />
                          <span style={{ fontSize: 12, color: 'var(--ink-faint)', flexShrink: 0 }}>
                            {g.prefix === 'P' ? '%' : 'kr'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <button
                          className="icon-button"
                          style={{ color: 'var(--ink-faint)' }}
                          title="Ta bort rad"
                          onClick={() => removeRow(item.sortOrder)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!q && (
                <div style={{ padding: '10px 14px' }}>
                  <button
                    className="icon-btn"
                    style={{ fontSize: 12.5 }}
                    onClick={() => addRow(g.prefix)}
                  >
                    <Plus size={13} />
                    Ny {g.prefix}-rad
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      })}

      {/* Add new group */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', borderTop: '.5px solid var(--border)' }}>
        <span style={{ fontSize: 12.5, color: 'var(--ink-dim)', marginRight: 4 }}>Ny grupp:</span>
        <input
          value={newPrefix}
          onChange={e => setNewPrefix(e.target.value.slice(0, 3))}
          placeholder="Prefix (t.ex. B)"
          style={{ width: 90, fontFamily: 'var(--mono)', fontSize: 13 }}
          className="field-input"
        />
        <input
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder="Benämning"
          style={{ width: 200, fontSize: 13 }}
          className="field-input"
          onKeyDown={e => e.key === 'Enter' && addGroup()}
        />
        <button
          className="btn-primary"
          onClick={addGroup}
          disabled={!newPrefix.trim() || !newLabel.trim() || groups.some(g => g.prefix === newPrefix.trim().toUpperCase())}
          style={{ fontSize: 12.5 }}
        >
          <Plus size={13} /> Lägg till grupp
        </button>
      </div>
    </div>
  )
}
